import api from '../../../utils/api';

export const toolkitService = {
  async getAreaGroups() {
    const response = await api.get('/area-groups/');
    return response.data;
  },

  async getAreaGroupOptions() {
    const response = await api.get('/area-groups/options/');
    return response.data;
  },

  async getAreaGroupRecommendations(groupId, payload) {
    const response = await api.post(`/area-groups/${groupId}/recommendations/`, payload);
    return response.data;
  },

  async createAreaGroup(payload) {
    const response = await api.post('/area-groups/', payload);
    return response.data;
  },

  async deleteAreaGroup(groupId) {
    await api.delete(`/area-groups/${groupId}/`);
  },

  async updateAreaGroup(groupId, payload) {
    const response = await api.patch(`/area-groups/${groupId}/`, payload);
    return response.data;
  },

  async shareAreaGroup(groupId, clubId) {
    const response = await api.post(`/area-groups/${groupId}/share/`, { club_id: clubId });
    return response.data;
  },

  async unshareAreaGroup(groupId) {
    const response = await api.post(`/area-groups/${groupId}/unshare/`);
    return response.data;
  },

  async detectAreaGroupZone(groupId) {
    const response = await api.post(`/area-groups/${groupId}/detect-zone/`);
    return response.data;
  },

  /**
   * Resolve USDA PHZM zone for a lat/lng (no saved group required).
   * Returns { usda_zone_code, zone_source, detail? }.
   */
  async lookupUsdaZone({ latitude, longitude }) {
    const response = await api.post('/area-groups/lookup-zone/', { latitude, longitude });
    return response.data;
  },

  /**
   * SSURGO soil attributes for a point (or polygon GeoJSON).
   * Returns { mu_name, attributes: { drainage_class, ph, ... }, ... }.
   */
  async lookupSoil({ latitude, longitude, state, geometry }) {
    const payload = {
      state,
      geometry: geometry || {
        type: 'Point',
        coordinates: [longitude, latitude],
      },
    };
    const response = await api.post('/soil/lookup/', payload);
    return response.data;
  },

  /**
   * Near-term plantability forecast for Timing (zone window + drainage + rain).
   * Returns { weather_ok, days, best_day, window, frost, ... }.
   */
  async createPlantingEvent(payload) {
    const response = await api.post('/planting-events/', payload);
    return response.data;
  },

  async getPlantingEvents() {
    const response = await api.get('/planting-events/');
    return response.data;
  },

  async deletePlantingEvent(eventId) {
    await api.delete(`/planting-events/${eventId}/`);
  },

  async getPlantingForecast({
    latitude,
    longitude,
    season,
    usdaZoneCode,
    soilDrainage,
    frostSource,
    lastFrostLabel,
    firstFrostLabel,
    days = 10,
  }) {
    const response = await api.post('/area-groups/planting-forecast/', {
      latitude,
      longitude,
      season,
      usda_zone_code: usdaZoneCode || '',
      soil_drainage: soilDrainage || '',
      frost_source: frostSource || '',
      last_frost_label: lastFrostLabel || '',
      first_frost_label: firstFrostLabel || '',
      days,
    });
    return response.data;
  },

  /**
   * Blend, cart, lime, and cost for the Plan step. No AreaGroup is created.
   * Agronomy lives on the backend so web and mobile read the same mix.
   */
  async getPlanPreview({ acres, wizardAnswers, place, blend } = {}) {
    const response = await api.post('/area-groups/plan-preview/', {
      acres: acres || 0,
      wizard_answers: wizardAnswers || {},
      place: place || {},
      blend: blend || '',
    });
    return response.data;
  },

  async findSupplyStores(payload) {
    const response = await api.post('/toolkit/stores/search/', payload);
    return response.data;
  },

  /**
   * Suggest places/POIs by name or address (Mapbox Search Box). Use in suggest-a-store form.
   * Returns { suggestions: [{ name, address, city, state, postal_code, latitude, longitude }] }.
   */
  async placeSuggest(query, { proximity, limit = 8 } = {}) {
    const params = new URLSearchParams({ q: query });
    if (proximity) params.set('proximity', proximity);
    if (limit) params.set('limit', limit);
    const response = await api.get(`/toolkit/stores/place-suggest/?${params.toString()}`);
    return response.data;
  },
};

